import { useSnackbar } from 'notistack';
import React, { createContext, useContext, ReactNode, useState } from 'react';
import { useCybozuUser } from 'src/repository/useCybozuUser';
import { getAuthClient } from 'src/lib/aws/cognitoAuthClient';

interface CybozuAuthContextProps {
  signIn: (organizationId: string) => Promise<void>;
  signOut: (userId: string) => Promise<void>;
  error: string | null;
  isLoading: boolean;
  clearError: () => void;
}

const CybozuAuthContext = createContext<CybozuAuthContextProps>({
  signIn: async () => {},
  signOut: async () => {},
  error: null,
  isLoading: false,
  clearError: () => {},
});

interface CybozuAuthProviderProps {
  children: ReactNode;
}

export const CybozuAuthProvider: React.FC<CybozuAuthProviderProps> = ({ children }) => {
  const { cancelGaroonIntegration } = useCybozuUser();
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const { enqueueSnackbar } = useSnackbar();

  const clearError = () => setError(null);

  const signIn = async (organizationId: string) => {
    try {
      setIsLoading(true);
      setError(null);

      // Acquire ID token to forward to API route for server-side GraphQL auth
      let idToken: string | undefined;
      try {
        const auth = getAuthClient();
        idToken = await auth.getValidIdToken();
      } catch {
        if (typeof window !== 'undefined') {
          idToken = localStorage.getItem('idToken') || undefined;
        }
      }

      const response = await fetch(`/api/cybozu/login?organizationId=${organizationId}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          ...(idToken ? { Authorization: `Bearer ${idToken}` } : {}),
        },
      });

      if (!response.ok) {
        const errorText = await response.text().catch(() => '');
        throw new Error(
          `サインインに失敗しました（${response.status}）: ${
            errorText || response.statusText || '不明なエラー'
          }`
        );
      }
      const res = await response.json();
      if (res && typeof res.authUrl === 'string' && res.authUrl.startsWith('https://')) {
        window.location.href = res.authUrl;
      } else {
        throw new Error('認証URLが無効です');
      }
    } catch (error) {
      setError(error instanceof Error ? error.message : 'An error occurred during sign in');
      console.error('Sign in error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const signOut = async (userId: string) => {
    try {
      setIsLoading(true);
      setError(null);
      await cancelGaroonIntegration(userId);
      enqueueSnackbar('連携を解除しました', { variant: 'success' });
      window.location.reload();
    } catch (error) {
      setError(error instanceof Error ? error.message : 'An error occurred during sign out');
      console.error('Sign out error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const value: CybozuAuthContextProps = {
    signIn,
    signOut,
    error,
    isLoading,
    clearError,
  };

  return <CybozuAuthContext.Provider value={value}>{children}</CybozuAuthContext.Provider>;
};

/**
 * Hook to access the CybozuAuthContext
 */
export const useCybozuAuthContext = (): CybozuAuthContextProps => {
  const context = useContext(CybozuAuthContext);
  if (!context) {
    throw new Error('useCybozuAuthContext must be used within a CybozuAuthProvider');
  }
  return context;
};
